import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, Building, Percent, Calendar, CreditCard, TrendingUp, Check, Gift, Copy, Users, Shield, ShieldCheck, Download, Trash2, BookOpen, FileText } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { QontoLogo } from "../components/integrations/QontoLogo";
import { UrssafSection } from "../components/urssaf/UrssafSection";
import { QONTO_ENABLED } from "../utils/featureFlags";
import { getCreationDuration, getAcreEndDate, getVLRate } from "../utils/helpers";
import { API } from "../utils/constants";
import axios from "axios";

// Settings Page
export const ParametresPage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [isAssujettiTVA, setIsAssujettiTVA] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportAnnee, setExportAnnee] = useState(new Date().getFullYear());

  const downloadExport = async (kind, format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("biz360_token");
      const res = await axios.get(
        `${API}/exports/${kind}?annee=${exportAnnee}&format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const extension = format === "pdf" ? "pdf" : "csv";
      const prefix = kind === "livre-recettes" ? "livre_recettes" : "registre_achats";
      link.setAttribute("download", `${prefix}_${exportAnnee}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${prefix.replace("_", " ")} ${exportAnnee} téléchargé 📄`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du téléchargement");
    }
    setExporting(false);
  };
  const api = useApi();
  const { user, updateUser, refreshUser, logout } = useAuth();
  const URSSAF_BETA_EMAILS = ["t.samblat@gmail.com", "paulttfrouzins@gmail.com"];
  const canSeeUrssaf = user?.email && URSSAF_BETA_EMAILS.includes(user.email.toLowerCase());
  const navigate = useNavigate();

  useEffect(() => { loadProfile(); loadReferral(); loadTVAStatus(); }, []);

  const loadProfile = async () => {
    try { const response = await api.get("/profile"); setProfile(response.data); } catch (err) { toast.error("Erreur"); }
    setLoading(false);
  };

  const loadTVAStatus = async () => {
    try { 
      const response = await api.get("/dashboard"); 
      setIsAssujettiTVA(response.data?.statut_tva?.assujetti || false);
    } catch (err) { console.error("Erreur chargement statut TVA:", err); }
  };

  const loadReferral = async () => {
    try { 
      const [codeRes, statsRes] = await Promise.all([
        api.get("/referral/my-code"),
        api.get("/referral/stats")
      ]);
      setReferralData(codeRes.data);
      setReferralStats(statsRes.data);
    } catch (err) { console.error("Erreur parrainage:", err); }
  };

  const copyReferralLink = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      toast.success("Lien copié !");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try { 
      const response = await api.put("/profile", profile); 
      // Utilise la réponse serveur (inclut les champs auto-syncés comme urssaf_periodicite)
      // au lieu de `profile` local, sinon les valeurs recalculées côté back sont perdues.
      if (response?.data) {
        setProfile(response.data);
        updateUser(response.data);
      } else {
        updateUser(profile); 
      }
      // Rafraîchit aussi le contexte auth global depuis /auth/me pour être sûr
      await refreshUser();
      toast.success("Paramètres enregistrés !"); 
    } catch (err) { 
      const message = err.response?.data?.detail || "Erreur";
      toast.error(message);
    }
    setSaving(false);
  };

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="parametres-page">
        <div><h1 className="text-2xl font-bold" style={{color: '#210875'}}>Mes Paramètres</h1><p className="text-muted-foreground">Je configure mon profil</p></div>

        {/* ==================== INTÉGRATION QONTO (en haut) ==================== */}
        <QontoIntegrationCard api={api} />

        <div className="bg-card rounded-xl border p-6 space-y-6">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#897FCB'}}><User size={20} /> Mes informations personnelles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-2">Prénom</label>
              <input type="text" value={profile?.prenom || ""} onChange={(e) => setProfile({ ...profile, prenom: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" />
            </div>
            <div><label className="block text-sm font-medium mb-2">Nom</label>
              <input type="text" value={profile?.nom || ""} onChange={(e) => setProfile({ ...profile, nom: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border p-6 space-y-6">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#897FCB'}}><Building size={20} /> Mon activité principale</h2>
          <div><label className="block text-sm font-medium mb-2">Date de création</label>
            <input type="date" value={profile?.date_creation_entreprise || ""} onChange={(e) => setProfile({ ...profile, date_creation_entreprise: e.target.value })} className="w-full h-11 px-4 rounded-lg border border-input bg-background" />
            {profile?.date_creation_entreprise && <p className="mt-2 text-sm text-emerald-600">{getCreationDuration(profile.date_creation_entreprise)}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-2">Type d'activité</label>
              <select 
                key="select-type-activite"
                value={profile?.type_activite || ""} 
                onChange={(e) => setProfile({ ...profile, type_activite: e.target.value })} 
                className="w-full h-11 px-4 rounded-lg border border-input bg-background"
              >
                <option value="">Sélectionner le type...</option>
                <option value="vente de marchandises">Vente de marchandises</option>
                <option value="prestation de services">Prestation de services</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-2">Activité</label>
              <select 
                key="select-activite"
                value={profile?.activite || ""} 
                onChange={(e) => setProfile({ ...profile, activite: e.target.value })} 
                className="w-full h-11 px-4 rounded-lg border border-input bg-background"
              >
                <option value="">Sélectionner l'activité...</option>
                <option value="artisanale">Artisanale</option>
                <option value="commerciale">Commerciale</option>
                <option value="libérale">Libérale</option>
                <option value="libérale réglementée">Libérale Réglementée</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="double_activite" checked={profile?.double_activite || false} onChange={(e) => setProfile({ ...profile, double_activite: e.target.checked })} className="w-5 h-5 rounded" />
            <label htmlFor="double_activite">J'ai une double activité</label>
          </div>
          {profile?.double_activite && (
            <div className="pl-6 border-l-2 border-primary/20 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-2">Type secondaire</label>
                  <select 
                    key="select-type-secondaire"
                    value={profile?.type_activite_secondaire || ""} 
                    onChange={(e) => setProfile({ ...profile, type_activite_secondaire: e.target.value })} 
                    className="w-full h-11 px-4 rounded-lg border border-input bg-background"
                  >
                    <option value="">Sélectionner le type...</option>
                    <option value="vente de marchandises">Vente de marchandises</option>
                    <option value="prestation de services">Prestation de services</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-2">Activité secondaire</label>
                  <select 
                    key="select-activite-secondaire"
                    value={profile?.activite_secondaire || ""} 
                    onChange={(e) => setProfile({ ...profile, activite_secondaire: e.target.value })} 
                    className="w-full h-11 px-4 rounded-lg border border-input bg-background"
                  >
                    <option value="">Sélectionner l'activité...</option>
                    <option value="artisanale">Artisanale</option>
                    <option value="commerciale">Commerciale</option>
                    <option value="libérale">Libérale</option>
                    <option value="libérale réglementée">Libérale Réglementée</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Frais plateforme - NOUVEAU */}
        <div className="bg-card rounded-xl border p-6 space-y-6" style={{borderLeft: '4px solid #F8BC91'}}>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#897FCB'}}><CreditCard size={20} /> Mes frais plateforme (Sumup / Stripe / etc.)</h2>
          <div className="p-4 rounded-lg" style={{backgroundColor: '#FDFCFF'}}>
            <p className="text-sm mb-4" style={{color: '#210875'}}>
              <strong>Réglementation française :</strong> Les frais de plateforme (Sumup, Stripe, PayPal...) ne sont <strong>pas</strong> déductibles du CA déclaré à l'Urssaf. 
              Tu dois déclarer le <strong>montant brut facturé</strong>, pas le montant net reçu.
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Pourcentage de frais (%)</label>
              <div className="flex items-center gap-4">
                <input 
                  type="number" 
                  value={profile?.frais_plateforme_pourcent || ""} 
                  onChange={(e) => setProfile({ ...profile, frais_plateforme_pourcent: parseFloat(e.target.value) || 0 })} 
                  className="w-32 h-11 px-4 rounded-lg border border-input bg-background" 
                  min="0" 
                  max="10" 
                  step="0.01"
                  placeholder="ex: 1.75"
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Exemples: Sumup ~1,75% / Stripe ~1,4% + 0,25€ / PayPal ~2,9%</p>
            </div>
          </div>
          {profile?.frais_plateforme_pourcent > 0 && (
            <div className="p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700">
              <Check size={16} className="inline mr-2" />
              Frais de {profile.frais_plateforme_pourcent}% configurés. Ils seront enregistrés dans tes dépenses pour ton suivi (sans impact sur le CA déclaré).
            </div>
          )}
        </div>

        {/* CA N-1 pour TVA - NOUVEAU */}
        <div className="bg-card rounded-xl border p-6 space-y-6" style={{borderLeft: '4px solid #FDFCFF'}}>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#897FCB'}}><TrendingUp size={20} /> Mon CA de l'année précédente (N-1)</h2>
          <div className="p-4 rounded-lg" style={{backgroundColor: '#FDFCFF40'}}>
            <p className="text-sm mb-4" style={{color: '#210875'}}>
              Le CA de l'année précédente est utilisé pour déterminer si vous passez au régime de TVA au 1er janvier de l'année suivante.
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">CA N-1 (€)</label>
              <input 
                type="number" 
                value={profile?.ca_n_moins_1 || ""} 
                onChange={(e) => setProfile({ ...profile, ca_n_moins_1: parseFloat(e.target.value) || 0 })} 
                className="w-48 h-11 px-4 rounded-lg border border-input bg-background" 
                min="0" 
                step="1"
                placeholder="ex: 25000"
              />
            </div>
          </div>
        </div>

        {/* SECTION RÉGIME TVA - UNIQUEMENT pour les assujettis */}
        {isAssujettiTVA && (
        <div className="bg-card rounded-xl border p-6 space-y-6" style={{borderLeft: '4px solid #E8A87C'}}>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#897FCB'}}>
            <TrendingUp size={20} /> Mon régime de TVA
          </h2>
          
          <div className="p-4 rounded-lg" style={{backgroundColor: '#E8A87C15'}}>
            <p className="text-sm mb-4" style={{color: '#210875'}}>
              Tu es assujetti à la TVA. Choisis ton régime de déclaration. Le <strong>réel simplifié</strong> est le régime par défaut.
            </p>
            
            <div className="space-y-3">
              {/* Réel Simplifié */}
              <button 
                type="button" 
                onClick={() => setProfile({ ...profile, regime_tva: "simplifie" })}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  profile?.regime_tva === "simplifie" || !profile?.regime_tva 
                    ? "border-[#E8A87C] bg-[#E8A87C]/10" 
                    : "border-input hover:border-[#E8A87C]/50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      Réel Simplifié
                      {(profile?.regime_tva === "simplifie" || !profile?.regime_tva) && (
                        <span className="text-xs px-2 py-0.5 rounded bg-[#059669] text-white">Par défaut</span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Déclaration annuelle <strong>CA12</strong>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      2 acomptes : juillet (55%) + décembre (40%)<br/>
                      Si TVA N-1 &lt; 1 000€ : paiement unique à la CA12
                    </div>
                  </div>
                  {(profile?.regime_tva === "simplifie" || !profile?.regime_tva) && (
                    <Check className="text-[#059669]" size={20} />
                  )}
                </div>
              </button>
              
              {/* Réel Normal Mensuel */}
              <button 
                type="button" 
                onClick={() => setProfile({ ...profile, regime_tva: "reel_normal_mensuel" })}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  profile?.regime_tva === "reel_normal_mensuel" 
                    ? "border-[#E8A87C] bg-[#E8A87C]/10" 
                    : "border-input hover:border-[#E8A87C]/50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">Réel Normal — Mensuel</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Déclaration <strong>CA3</strong> chaque mois
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Recommandé si TVA annuelle estimée &gt; 4 000€
                    </div>
                  </div>
                  {profile?.regime_tva === "reel_normal_mensuel" && (
                    <Check className="text-[#059669]" size={20} />
                  )}
                </div>
              </button>
              
              {/* Réel Normal Trimestriel */}
              <button 
                type="button" 
                onClick={() => setProfile({ ...profile, regime_tva: "reel_normal_trimestriel" })}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  profile?.regime_tva === "reel_normal_trimestriel" 
                    ? "border-[#E8A87C] bg-[#E8A87C]/10" 
                    : "border-input hover:border-[#E8A87C]/50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">Réel Normal — Trimestriel</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Déclaration <strong>CA3</strong> chaque trimestre
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Si TVA annuelle estimée ≤ 4 000€
                    </div>
                  </div>
                  {profile?.regime_tva === "reel_normal_trimestriel" && (
                    <Check className="text-[#059669]" size={20} />
                  )}
                </div>
              </button>
            </div>
            
            <div className="mt-4 p-3 rounded-lg text-xs" style={{backgroundColor: '#FEF3C7', border: '1px solid #F59E0B'}}>
              <strong>⚠️ Ce choix engage 2 ans minimum</strong><br/>
              L'option pour le réel normal doit être formulée auprès de ton SIE (Service des Impôts des Entreprises).
            </div>
          </div>
        </div>
        )}

        <div className="bg-card rounded-xl border p-6 space-y-6">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#897FCB'}}><Percent size={20} /> Mes options fiscales</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-lg border">
              <input type="checkbox" id="vl" checked={profile?.versement_liberatoire || false} onChange={(e) => setProfile({ ...profile, versement_liberatoire: e.target.checked })} className="w-5 h-5 rounded mt-0.5" />
              <div><label htmlFor="vl" className="font-medium">Versement libératoire</label>
                {profile?.versement_liberatoire && profile?.type_activite && <p className="text-sm text-blue-600 mt-1">Taux: {getVLRate(profile.type_activite, profile.activite).display}</p>}
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-lg border">
              <input type="checkbox" id="acre" checked={profile?.acre || false} onChange={(e) => setProfile({ ...profile, acre: e.target.checked })} className="w-5 h-5 rounded mt-0.5" />
              <div><label htmlFor="acre" className="font-medium">ACRE</label>
                {profile?.acre && profile?.date_creation_entreprise && <p className="text-sm text-emerald-600 mt-1">Jusqu'au: {getAcreEndDate(profile.date_creation_entreprise)}</p>}
              </div>
            </div>
          </div>
        </div>

        {canSeeUrssaf ? (
          <UrssafSection profile={profile} setProfile={setProfile} />
        ) : (
          <div
            className="rounded-xl p-6 flex items-start gap-4"
            style={{backgroundColor: '#FDFCFF', border: '2px dashed #897FCB'}}
            data-testid="urssaf-teasing-section"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{backgroundColor: '#EDE9FE'}}>
              <ShieldCheck size={24} style={{color: '#897FCB'}} />
            </div>
            <div className="flex-1 space-y-2">
              <h2 className="text-lg font-semibold" style={{color: '#897FCB'}}>
                Mon espace Urssaf — bientôt disponible 🚀
              </h2>
              <p className="text-sm text-muted-foreground">
                L'intégration officielle avec l'API Urssaf (TDAE) est en phase finale de certification auprès de l'ACOSS.
                Dès sa mise en service, tu pourras déclarer ton CA et payer tes cotisations en 1 clic depuis Biz360,
                sans jamais quitter l'application.
              </p>
              <p className="text-sm font-medium" style={{color: '#210875'}}>
                📬 Tu seras prévenu(e) par email dès l'ouverture — pas d'action requise de ta part.
              </p>
            </div>
          </div>
        )}

        {/* SECTION PARRAINAGE */}
        <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-xl border-2 border-purple-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: '#8a7fce'}}>
            <Gift size={20} /> Programme de Parrainage
          </h2>
          
          <div className="bg-white rounded-lg p-4 border">
            <p className="text-sm text-muted-foreground mb-3">
              🎁 <strong>Tu recommandes Biz360 ?</strong><br/>
              Ta filleule teste 1 mois gratuitement.<br/>
              Si elle s'abonne, <strong>tu gagnes 1 mois offert</strong> !
            </p>
            
            {referralData && (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">Ton code parrainage</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="bg-purple-100 text-purple-700 px-3 py-2 rounded-lg font-bold text-lg flex-1">
                      {referralData.referral_code}
                    </code>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground">Ton lien de parrainage</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input 
                      type="text" 
                      readOnly 
                      value={referralData.referral_link} 
                      className="flex-1 h-10 px-3 rounded-lg border bg-gray-50 text-sm"
                    />
                    <button 
                      onClick={copyReferralLink}
                      className="h-10 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Copy size={16} /> Copier
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {referralStats && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 text-center border">
                <div className="text-2xl font-bold text-purple-600">{referralStats.total}</div>
                <div className="text-xs text-muted-foreground">Filleul(e)s</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border">
                <div className="text-2xl font-bold text-green-600">{referralStats.converted}</div>
                <div className="text-xs text-muted-foreground">Converti(e)s</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center border">
                <div className="text-2xl font-bold text-orange-500">{referralStats.months_earned}</div>
                <div className="text-xs text-muted-foreground">Mois gagnés</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving} className="px-6 h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>

        {/* ==================== ABONNEMENT STRIPE ==================== */}
        <div className="bg-card border rounded-xl p-6 space-y-4" data-testid="subscription-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                <CreditCard size={20} className="text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold">Mon abonnement</h3>
                <p className="text-xs text-muted-foreground">Moyen de paiement, factures, résiliation.</p>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await api.post("/subscription/portal");
                  if (res.data?.url) {
                    window.location.href = res.data.url;
                  } else {
                    toast.error("Impossible d'ouvrir le portail Stripe pour le moment.");
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erreur lors de l'ouverture du portail Stripe.");
                }
              }}
              className="h-10 px-4 rounded-lg text-white font-medium text-sm transition hover:opacity-90"
              style={{ backgroundColor: "#210875" }}
              data-testid="stripe-portal-btn"
            >
              Gérer mon abonnement →
            </button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Tu peux mettre à jour ta carte bancaire, télécharger tes factures Stripe ou résilier ton abonnement
            à tout moment depuis le portail sécurisé Stripe.
          </p>
        </div>

        {/* ==================== RGPD ==================== */}
        <div className="bg-card border-2 border-dashed rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Shield size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Mes données personnelles (RGPD)</h3>
              <p className="text-xs text-muted-foreground">Tu as un contrôle total sur tes données.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={async () => {
                setExporting(true);
                try {
                  const token = localStorage.getItem("biz360_token");
                  const res = await axios.get(`${API}/profile/export-data`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute("download", `biz360_export_${new Date().toISOString().slice(0,10)}.zip`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success("Tes données ont été téléchargées 📦");
                } catch (err) {
                  toast.error("Erreur lors du téléchargement");
                }
                setExporting(false);
              }}
              disabled={exporting}
              data-testid="rgpd-export-btn"
              className="flex items-center justify-center gap-2 px-4 h-11 rounded-lg border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors text-sm font-medium text-purple-700 disabled:opacity-50"
            >
              <Download size={16} />
              {exporting ? "Génération..." : "Télécharger toutes mes données"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/politique-confidentialite")}
              data-testid="rgpd-policy-btn"
              className="flex items-center justify-center gap-2 px-4 h-11 rounded-lg border bg-white hover:bg-muted transition-colors text-sm font-medium"
            >
              <Shield size={16} /> Politique de confidentialité
            </button>
          </div>

          <div className="pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              data-testid="rgpd-delete-btn"
              className="flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
            >
              <Trash2 size={14} /> Supprimer mon compte
            </button>
            <p className="text-xs text-muted-foreground mt-1">
              Suppression effective sous 30 jours. Tu peux annuler pendant ce délai.
            </p>
          </div>
        </div>

        {/* ==================== DOCUMENTS LÉGAUX ==================== */}
        <div className="bg-card border-2 border-dashed rounded-xl p-6 space-y-4" data-testid="documents-legaux-section">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <BookOpen size={20} className="text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold">Documents comptables obligatoires</h3>
              <p className="text-xs text-muted-foreground">
                Livre de recettes et registre des achats conformes aux obligations légales du micro-entrepreneur.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">Année</label>
            <select
              value={exportAnnee}
              onChange={(e) => setExportAnnee(parseInt(e.target.value))}
              className="w-full sm:w-48 h-10 px-3 rounded-lg border bg-white text-sm"
              data-testid="export-annee-select"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
              <div className="font-medium text-sm text-indigo-900 flex items-center gap-2">
                <BookOpen size={14} /> Livre de recettes
              </div>
              <p className="text-xs text-indigo-800 leading-relaxed">
                Toutes tes ventes encaissées de l'année, dans l'ordre chronologique.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => downloadExport("livre-recettes", "pdf")}
                  disabled={exporting}
                  data-testid="export-livre-pdf"
                  className="flex-1 flex items-center justify-center gap-1 h-9 rounded-md border border-indigo-300 bg-white hover:bg-indigo-100 text-xs font-medium text-indigo-800 disabled:opacity-50"
                >
                  <FileText size={13} /> PDF
                </button>
                <button
                  type="button"
                  onClick={() => downloadExport("livre-recettes", "csv")}
                  disabled={exporting}
                  data-testid="export-livre-csv"
                  className="flex-1 flex items-center justify-center gap-1 h-9 rounded-md border border-indigo-300 bg-white hover:bg-indigo-100 text-xs font-medium text-indigo-800 disabled:opacity-50"
                >
                  <Download size={13} /> CSV
                </button>
              </div>
            </div>

            <div className="space-y-2 p-3 rounded-lg bg-rose-50 border border-rose-100">
              <div className="font-medium text-sm text-rose-900 flex items-center gap-2">
                <BookOpen size={14} /> Registre des achats
              </div>
              <p className="text-xs text-rose-800 leading-relaxed">
                Toutes tes dépenses pro de l'année (obligatoire pour les activités d'achat-revente).
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => downloadExport("registre-achats", "pdf")}
                  disabled={exporting}
                  data-testid="export-registre-pdf"
                  className="flex-1 flex items-center justify-center gap-1 h-9 rounded-md border border-rose-300 bg-white hover:bg-rose-100 text-xs font-medium text-rose-800 disabled:opacity-50"
                >
                  <FileText size={13} /> PDF
                </button>
                <button
                  type="button"
                  onClick={() => downloadExport("registre-achats", "csv")}
                  disabled={exporting}
                  data-testid="export-registre-csv"
                  className="flex-1 flex items-center justify-center gap-1 h-9 rounded-md border border-rose-300 bg-white hover:bg-rose-100 text-xs font-medium text-rose-800 disabled:opacity-50"
                >
                  <Download size={13} /> CSV
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Documents conformes à l'article L123-28 du Code de commerce. À conserver 10 ans en cas de contrôle fiscal.
          </p>
        </div>

        {/* Modale suppression */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="delete-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-xl font-bold text-red-600">⚠️ Supprimer mon compte</h3>
              <p className="text-sm text-muted-foreground">
                Cette action <strong>désactive immédiatement</strong> ton compte. Tes données seront définitivement supprimées sous <strong>30 jours</strong>.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                <strong>Important :</strong> Conformément à la loi française, certaines données comptables (factures, déclarations) seront conservées 10 ans même après suppression.
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Ton mot de passe</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="delete-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tape <code className="bg-red-50 text-red-700 px-1 rounded">SUPPRIMER</code> pour confirmer</label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500"
                  data-testid="delete-confirmation"
                  placeholder="SUPPRIMER"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowDeleteModal(false); setDeletePassword(""); setDeleteConfirmation(""); }}
                  className="flex-1 h-11 rounded-lg border bg-white hover:bg-muted font-medium"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  data-testid="delete-confirm-btn"
                  disabled={deleting || !deletePassword || deleteConfirmation.toUpperCase() !== "SUPPRIMER"}
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      const res = await api.post("/profile/delete-request", {
                        password: deletePassword,
                        confirmation: deleteConfirmation,
                      });
                      toast.success(res.data.message);
                      setTimeout(() => { logout(); navigate("/"); }, 1500);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Erreur");
                      setDeleting(false);
                    }
                  }}
                  className="flex-1 h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50"
                >
                  {deleting ? "Traitement..." : "Confirmer la suppression"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};


// ====================================================================
// QontoIntegrationCard — Section Paramètres → Intégration Qonto
// ====================================================================
const QontoIntegrationCard = ({ api }) => {
  // ── Feature flag : Qonto désactivé en prod tant que le partenariat n'est pas signé ──
  if (!QONTO_ENABLED) {
    return null; // Card Factur-X retirée sur demande utilisateur
  }
  return <QontoActiveIntegrationCard api={api} />;
};

const QontoActiveIntegrationCard = ({ api }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncStats, setLastSyncStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/qonto/status");
      setStatus(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (loading || !status) {
    return null;
  }

  const { configured, connected, organization, consent, consent_text, webhook } = status;
  const webhookActive = !!webhook?.active;

  const handleAcceptConsent = async () => {
    setSubmitting(true);
    try {
      await api.post("/qonto/consent", { accepted: true });
      toast.success("Consentement enregistré ✅");
      setShowConsent(false);
      await load();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement du consentement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConnectExisting = async () => {
    if (!configured) {
      toast.info("L'intégration Qonto sera bientôt disponible (en attente d'activation).");
      return;
    }
    if (!consent.given) {
      setShowConsent(true);
      return;
    }
    try {
      const res = await api.get("/qonto/oauth/start", { params: { origin: window.location.origin } });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("URL Qonto manquante");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const handleOnboarding = async () => {
    if (!consent.given) {
      setShowConsent(true);
      return;
    }
    try {
      const res = await api.post("/qonto/onboarding/start", {});
      if (res.data?.redirection_link) {
        window.open(res.data.redirection_link, "_blank", "noopener");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Déconnecter votre compte Qonto ? Les imports automatiques s'arrêteront.")) return;
    try {
      await api.delete("/qonto/connection");
      toast.success("Compte Qonto déconnecté");
      await load();
    } catch {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setLastSyncStats(null);
    try {
      const res = await api.post("/qonto/sync", {});
      const s = res.data?.stats || res.data || {};
      setLastSyncStats(s);
      const created = (s.suppliers?.created || 0) + (s.clients?.created || 0);
      const updated = (s.suppliers?.updated || 0) + (s.clients?.updated || 0);
      const removed = (s.suppliers?.removed || 0) + (s.clients?.removed || 0);
      if (created + updated + removed === 0) {
        toast.info("Aucune modification (tout est déjà à jour).");
      } else {
        toast.success(`Sync OK : ${created} créée(s), ${updated} mise(s) à jour, ${removed} supprimée(s).`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la synchronisation Qonto";
      toast.error(msg);
      // Si 401 → la connexion a été nettoyée côté serveur, on recharge le statut
      if (err?.response?.status === 401) {
        await load();
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleWebhook = async () => {
    try {
      if (webhookActive) {
        if (!window.confirm("Désactiver la sync automatique ? Tu devras cliquer 'Synchroniser maintenant' pour récupérer les nouvelles factures.")) return;
        await api.delete("/qonto/webhook/subscribe");
        toast.success("Synchro automatique désactivée");
      } else {
        await api.post("/qonto/webhook/subscribe", {}, { params: { origin: window.location.origin } });
        toast.success("✨ Synchro automatique activée — tes nouvelles factures arriveront en temps réel !");
      }
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'activation";
      toast.error(msg);
      if (err?.response?.status === 401) {
        await load();
      }
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden border shadow-sm"
      data-testid="qonto-section"
      style={{ background: "linear-gradient(135deg, #F8F5FF 0%, #FFF1E0 100%)", borderColor: "#D6CCFF" }}
    >
      <div className="p-5 sm:p-6">
        {/* Bandeau co-brand */}
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="font-bold text-lg tracking-tight inline-flex items-center gap-1" style={{ color: "#210875" }}>
              Biz360 <span aria-hidden>❤️‍🔥</span>
            </span>
            <span className="text-xl text-muted-foreground">×</span>
            <QontoLogo size={24} />
            {connected && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                ✓ Connecté
              </span>
            )}
          </div>

          <div className="flex-1 min-w-[200px]">
            <h3 className="text-base font-bold leading-tight" style={{ color: "#1a1a2e" }}>
              {connected
                ? "Tes factures arrivent en direct dans Biz360 ❤️‍🔥"
                : "Zéro saisie. Tes factures Qonto remontent dans Biz360 ❤️‍🔥"}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {connected
                ? `Compte connecté : ${organization?.name || "Qonto"}`
                : "Connecte ton Qonto et oublie la saisie manuelle de tes dépenses et ventes."}
            </p>
          </div>
        </div>

        {connected && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {organization?.connected_at && (
                  <>Liaison active depuis le {new Date(organization.connected_at).toLocaleDateString("fr-FR")}</>
                )}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSyncNow}
                  disabled={syncing}
                  data-testid="qonto-sync-now-btn"
                  className="h-9 px-4 rounded-lg text-white text-xs font-semibold transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  style={{ backgroundColor: "#210875" }}
                >
                  {syncing ? (
                    <>
                      <span className="inline-block h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Synchronisation…
                    </>
                  ) : (
                    <>↻ Synchroniser maintenant</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDisconnect}
                  data-testid="qonto-disconnect-btn"
                  className="text-xs text-red-700 underline hover:no-underline"
                >
                  Déconnecter
                </button>
              </div>
            </div>
            {lastSyncStats && (
              <div className="text-xs rounded-lg border bg-white/70 p-3" data-testid="qonto-sync-stats">
                <div className="font-semibold mb-1" style={{ color: "#210875" }}>Dernière synchro</div>
                <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                  <div>
                    Ventes (factures clients) : <span className="font-semibold text-green-700">{lastSyncStats.clients?.created || 0} créée(s)</span>
                    {lastSyncStats.clients?.skipped > 0 && <span> · {lastSyncStats.clients.skipped} déjà importée(s)</span>}
                  </div>
                  <div>
                    Dépenses (factures fournisseurs) : <span className="font-semibold text-green-700">{lastSyncStats.suppliers?.created || 0} créée(s)</span>
                    {lastSyncStats.suppliers?.skipped > 0 && <span> · {lastSyncStats.suppliers.skipped} déjà importée(s)</span>}
                  </div>
                </div>
              </div>
            )}

            {/* Toggle Synchro temps réel (webhooks Qonto) */}
            <div
              className="text-xs rounded-lg border bg-white/70 p-3 flex items-center justify-between gap-3 flex-wrap"
              data-testid="qonto-webhook-toggle"
            >
              <div className="flex items-start gap-2 flex-1 min-w-[200px]">
                <span className="text-base">{webhookActive ? "⚡" : "🔌"}</span>
                <div>
                  <div className="font-semibold" style={{ color: "#210875" }}>
                    {webhookActive ? "Synchro temps réel ACTIVÉE" : "Synchro temps réel"}
                  </div>
                  <div className="text-muted-foreground mt-0.5 leading-snug">
                    {webhookActive
                      ? "Tes nouvelles factures Qonto arrivent dans Biz360 en quasi temps réel, sans cliquer."
                      : "Active-la pour que tes factures Qonto remontent automatiquement (ventes en temps réel, dépenses en moins de 2 minutes)."}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleToggleWebhook}
                data-testid="qonto-webhook-toggle-btn"
                className={`h-8 px-3 rounded-lg text-xs font-semibold transition ${webhookActive ? "bg-white border text-red-700 hover:bg-red-50" : "text-white hover:opacity-90"}`}
                style={!webhookActive ? { backgroundColor: "#210875" } : { borderColor: "#dc2626" }}
              >
                {webhookActive ? "Désactiver" : "Activer la synchro auto"}
              </button>
            </div>
          </div>
        )}

        {!connected && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleConnectExisting}
              disabled={!configured}
              data-testid="qonto-connect-existing-btn"
              className="flex-1 sm:flex-initial h-10 px-5 rounded-lg text-white text-sm font-semibold transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#210875" }}
            >
              Je connecte Qonto
            </button>
            <button
              type="button"
              onClick={handleOnboarding}
              data-testid="qonto-onboarding-btn"
              className="h-10 px-4 rounded-lg text-sm font-medium border transition hover:bg-white"
              style={{ borderColor: "#897FCB", color: "#210875", backgroundColor: "rgba(255,255,255,0.6)" }}
            >
              Je n'ai pas encore Qonto →
            </button>
          </div>
        )}

        {!configured && (
          <p className="text-xs text-muted-foreground italic mt-3">
            🔧 Activation en cours côté serveur. Bientôt cliquable.
          </p>
        )}
      </div>

      {/* Modal de consentement RGPD */}
      {showConsent && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="qonto-consent-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-bold inline-flex items-center gap-1" style={{ color: "#210875" }}>
              🔐 Avant de continuer
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
              {consent_text}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowConsent(false)}
                className="px-4 h-10 rounded-lg border hover:bg-gray-50 text-sm"
                data-testid="qonto-consent-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleAcceptConsent}
                disabled={submitting}
                className="px-4 h-10 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: "#210875" }}
                data-testid="qonto-consent-accept"
              >
                {submitting ? "..." : "J'accepte et je continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ====================================================================
// FacturXComingSoonCard — captation d'intérêt pour l'intégration
// Facturation Électronique (Factur-X, obligation septembre 2027).
// Affichée à la place de l'ancienne carte Qonto (partenariat abandonné
// car Qonto = compte bancaire payant ≥ 11€/mois pour les abonnés).
// ====================================================================
const FacturXComingSoonCard = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const api = useApi();

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/factur-x/early-access", { email, source: "parametres" });
    } catch {
      // silencieux : la captation reste cosmétique
    }
    setSubmitted(true);
    toast.success("✨ On te prévient dès qu'un partenaire Factur-X est intégré !");
  };

  return (
    <div
      className="rounded-xl overflow-hidden border shadow-sm relative"
      data-testid="facturx-coming-soon"
      style={{ background: "linear-gradient(135deg, #F8F5FF 0%, #FFF1E0 100%)", borderColor: "#D6CCFF" }}
    >
      <div
        className="absolute top-4 right-4 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: "#210875", color: "white" }}
      >
        ✨ Bientôt
      </div>

      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3 flex-wrap mb-4">
          <div className="flex-1 min-w-[200px]">
            <h3 className="text-base font-bold leading-tight" style={{ color: "#1a1a2e" }}>
              Facturation électronique — Septembre 2027
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Au 1<sup>er</sup> septembre 2027, l'émission de factures électroniques deviendra obligatoire pour tous les auto-entrepreneurs.
            </p>
          </div>
        </div>

        <div className="text-xs text-muted-foreground leading-relaxed mb-4 space-y-1.5">
          <p>
            <strong style={{ color: "#210875" }}>Notre engagement :</strong> intégrer un partenaire
            Factur-X <strong>gratuit pour ton abonnement Biz360</strong>, sans surcoût caché.
          </p>
          <p>
            En attendant, Biz360 te prépare déjà : tes factures Factur-X reçues seront stockées,
            déchiffrées et classées automatiquement dans tes dépenses dès septembre 2026.
          </p>
        </div>

        {submitted ? (
          <div
            className="p-3 rounded-lg text-sm text-center"
            style={{ backgroundColor: "#F8BC9120", color: "#210875" }}
            data-testid="facturx-early-access-confirmed"
          >
            ✅ Merci, on te prévient dès qu'un partenaire Factur-X est intégré dans Biz360 !
          </div>
        ) : (
          <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ton email pour être prévenu·e"
              className="flex-1 h-10 px-3 rounded-lg border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="facturx-early-access-email"
            />
            <button
              type="submit"
              className="h-10 px-4 rounded-lg text-white text-sm font-semibold transition hover:opacity-90"
              style={{ backgroundColor: "#210875" }}
              data-testid="facturx-early-access-submit"
            >
              Me prévenir
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Conservé en alias pour rétrocompatibilité avec les références existantes dans la page.
const QontoComingSoonCard = FacturXComingSoonCard;

